import React, { useEffect, useState } from "react";
import {
  adminListDisputes, adminResolveDispute, adminForceReverse,
} from "../../lib/api";
import { DisputeReviewModal } from "./DisputeReviewModal";

/**
 * DisputesSection — admin review queue for disputed/reversed/voided matches.
 * Renders a tabbed list + REVIEW modal that can dismiss / void / flip a result.
 */
export function DisputesSection({ onToast, setAuthed }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("open");
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState(null);   // challenge dict | null
  const [note, setNote] = useState("");

  const load = async (s = status) => {
    setBusy(true);
    try {
      const data = await adminListDisputes(s);
      setRows(data || []);
    } catch (e) {
      if (e?.response?.status === 401) setAuthed?.(false);
      else onToast?.("Couldn't load disputes.");
    } finally { setBusy(false); }
  };

  useEffect(() => { load(status); /* eslint-disable-next-line */ }, [status]);

  const dismiss = async (c) => {
    if (!window.confirm("Dismiss this dispute? The recorded result will stand.")) return;
    try {
      await adminResolveDispute(c.challenge_id, note);
      onToast?.("✅ Dispute dismissed.");
      setReviewing(null); setNote("");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't dismiss.");
    }
  };

  const voidIt = async (c) => {
    if (!window.confirm("Void this match? Score events + MMR will be unwound.")) return;
    try {
      await adminForceReverse(c.challenge_id, { void: true, note });
      onToast?.("🗑 Match voided.");
      setReviewing(null); setNote("");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't void.");
    }
  };

  const flip = async (c, new_winner_team_id) => {
    if (!window.confirm("Flip the winner of this match?")) return;
    try {
      await adminForceReverse(c.challenge_id, { new_winner_team_id, note });
      onToast?.("🔄 Winner flipped + MMR re-applied.");
      setReviewing(null); setNote("");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't flip.");
    }
  };

  return (
    <div data-testid="admin-disputes-section">
      <div className="flex gap-1 mb-4" data-testid="admin-disputes-tabs">
        {[
          { id: "open",     label: "OPEN" },
          { id: "reversed", label: "REVERSED" },
          { id: "voided",   label: "VOIDED" },
          { id: "all",      label: "ALL" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setStatus(t.id)}
            data-testid={`admin-dispute-tab-${t.id}`}
            className={`text-[11px] tracking-widest px-4 py-2 rounded-xl ${
              status === t.id ? "btn-neon" : "btn-ghost"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {busy ? (
        <p className="text-[11px] tracking-widest text-[#14b8a6] animate-pulse">LOADING…</p>
      ) : rows.length === 0 ? (
        <p className="text-[11px] text-white/45" data-testid="admin-disputes-empty">
          No disputes in this view.
        </p>
      ) : (
        <div className="space-y-3" data-testid="admin-disputes-list">
          {rows.map((c) => (
            <div
              key={c.challenge_id}
              data-testid={`admin-dispute-row-${c.challenge_id}`}
              className="glass rounded-2xl p-4 border border-[#14b8a6]/30"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="text-[10px] tracking-widest text-[#14b8a6] font-bold">
                    {c.status === "voided" ? "🗑 VOIDED" : c.reversed_at ? "🔄 REVERSED" : "🚩 OPEN DISPUTE"}
                  </p>
                  <p className="text-white text-sm mt-1 truncate">
                    <b>{(c.team_a || {}).name || "—"}</b>
                    <span className="text-white/45 mx-2 font-mono">{c.score_a ?? "?"}-{c.score_b ?? "?"}</span>
                    <b>{(c.team_b || {}).name || "—"}</b>
                  </p>
                  <p className="text-[10px] text-white/45 mt-1">
                    {c.league?.name || "—"} · {c.league?.game || "—"} ·
                    opened by {c.dispute_opener?.name || c.dispute_opened_by || "—"}
                  </p>
                  {c.dispute_reason && (
                    <p className="text-[11px] text-white/65 mt-2 italic">
                      "{c.dispute_reason}"
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  {c.status !== "voided" && c.disputed && (
                    <button
                      type="button"
                      onClick={() => setReviewing(c)}
                      data-testid={`admin-dispute-review-${c.challenge_id}`}
                      className="text-[10px] tracking-widest px-3 py-2 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25"
                    >
                      <i className="fas fa-gavel mr-1" />REVIEW
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {reviewing && (
        <DisputeReviewModal
          challenge={reviewing}
          note={note}
          setNote={setNote}
          onClose={() => { setReviewing(null); setNote(""); }}
          onDismiss={dismiss}
          onVoid={voidIt}
          onFlip={flip}
        />
      )}
    </div>
  );
}
